import React, { useState, FC } from "react";
import {
  DPTable,
  DPEmptyTable,
  DPButton,
  DPPageWrapper,
  DPPermissionGate,
} from "Components/DP";
import { useQuery } from "@apollo/client";
import queries from "Shared/queries";
import { Header } from "Components/DP/DPTable/DPTable";
import { GetAdminsQuery, GetAdminsQueryVariables } from "generated/graphql";
import useStyles from "./styles";
import CreateAdminModal from "../CreateAdminModal/CreateAdminModal";
import AdminTableRowContainer from "../AdminTableRowContainer/AdminTableRowContainer";
import { GetEmployeeProfilesQueryProps } from "Shared/types/EmployeeProfiles";
import { usePartnerConfig } from "Hooks/usePartnerConfig";
import DPHeadCellWithTooltip from "Components/DP/DPHeadCell/DPHeadCell";
import TooltipContent from "./AccessLevelHeadCell/TooltipContent/TooltipContent";

const adminTableHeaders: Header[] = [
  "Name",
  "E-mail",
  "Pay Group(s)",
  "Location(s)",
  <DPHeadCellWithTooltip label="Access Level" content={<TooltipContent/>} />,
  "Status",
];

const adminTableHeadersWithUpdateStatusColumn = [...adminTableHeaders, ""];

type Props = {
  goToPage: (queryProps: GetEmployeeProfilesQueryProps) => void;
  queryProps: GetEmployeeProfilesQueryProps;
  title: string;
};

const AdminsAndRolesTable: FC<Props> = ({ goToPage, queryProps, title }) => {
  const [isCreateAdminModalOpen, setIsCreateAdminModalOpen] = useState(false);
  const classes = useStyles();

  const { page = 1, ...restQueryProps } = queryProps || {};

  const onChangePageFn = (
    evt: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => goToPage({ ...restQueryProps, page: newPage });

  const { data, loading } = useQuery<GetAdminsQuery, GetAdminsQueryVariables>(
    queries.GET_ADMINS_QUERY,
    {
      variables: { page },
      fetchPolicy: "cache-and-network",
    },
  );

  const toggleCreateAdminModal = () => {
    setIsCreateAdminModalOpen(!isCreateAdminModalOpen);
  };

  const admins = data && data.managers && data.managers.items;

  const { permissions } = usePartnerConfig();

  const headers = permissions.canUpdateAdminStatus
    ? adminTableHeadersWithUpdateStatusColumn
    : adminTableHeaders;

  return (
    <DPPageWrapper title={title}>
      <DPPermissionGate permissionKeys={["canCreateAdmins"]}>
        <DPButton onClick={toggleCreateAdminModal} className={classes.button}>
          Create Admin
        </DPButton>
      </DPPermissionGate>
      <CreateAdminModal
        page={page}
        isOpen={isCreateAdminModalOpen}
        onClose={toggleCreateAdminModal}
      />
      <DPTable
        count={(data && data.managers && data.managers.totalCount) || 0}
        headers={headers}
        loading={loading}
        onChangePage={onChangePageFn}
        page={page}
        rowsPerPage={+process.env.REACT_APP_ADMIN_ROWS_PER_PAGE!}
      >
        {admins?.length
          ? (
            admins.map(admin => (
              <AdminTableRowContainer key={admin.id} admin={admin} page={page} />
            ))
          )
          : <DPEmptyTable message="Admins will appear when set up by a DailyPay representative." />
        }
      </DPTable>
    </DPPageWrapper>
  );
};

export default AdminsAndRolesTable;
